// Share-web app state — pub/sub store.
//
// Mirrors the desktop `app/renderer/state.js` idiom but with a typed,
// callback-based subscribe API (Set of listeners, idempotent unsubscribe).
// `setState` shallow-merges a patch into the state, then notifies every
// subscriber AFTER the mutation so callbacks always observe the new value.

import type { ShareManifest, SummaryPeaks } from '@bandway/share-types';
import type { SetlistItem } from './setlist.js';

export interface TrackUiState {
  id: string;
  gain: number;
  muted: boolean;
  soloed: boolean;
}

export interface AppState {
  manifest: ShareManifest | null;
  peaks: SummaryPeaks | null;
  loaded: boolean;
  playing: boolean;
  playhead: number;
  trackStates: Map<string, TrackUiState>;
  currentSectionId: string | null;
  error: { kind: string; message?: string } | null;
  // Promo modal slot — F.1+F.2+F.3 shell. Topbar nav buttons set
  // {open:true, feature:<key>}; G.1 mounts the actual modal listening on it.
  promoModal: { open: boolean; feature?: string };
  // 53-я shell additions
  sidebarOpen: boolean;
  mode: 'rehearsal' | 'stage';
  shareDialog: { open: boolean };
  /**
   * SP3 setlist navigation. When the player was entered from a published
   * setlist (`/{shareId}?setlist=:id`), this holds the ordered items so the
   * setlist-rail can render them. Empty array = single-share context (promo
   * sidebar instead of the rail).
   */
  setlistItems: SetlistItem[];
  /** Name of the published setlist (shown in the rail header). Null = no setlist context. */
  setlistName: string | null;
  /** ISO createdAt of the published setlist (shown as the rail header date). */
  setlistCreatedAt: string | null;
  /** shareId of the song currently mounted in the player. Drives rail highlight. */
  activeShareId: string | null;
  /**
   * The reader is in full-screen chart reading (player-view/chart-fullscreen.ts).
   *
   * Lives OUT here rather than in the player because a setlist switch tears the
   * whole player region down and mounts it again (main.ts: disposeSong →
   * unmount → mountPlayerView). Anything kept inside the mount dies with it, so
   * the reading surface would collapse back to the mixer on the very button the
   * layer offers for moving between songs — which is what that button is for.
   * player-view/chart-surfaces.ts reads this synchronously on mount (restore)
   * and writes it on open/close.
   *
   * Nothing resets it on the way OUT of the player, and that is deliberate:
   * "Back to library" is a full navigation (ui/rail-library-button.ts →
   * location.assign('/library')), so the module is re-created and the flag is
   * false again by construction. The only in-app transition is the song switch,
   * which is exactly what it must survive.
   */
  chartFullscreenOpen: boolean;
}

function makeInitialState(): AppState {
  return {
    manifest: null,
    peaks: null,
    loaded: false,
    playing: false,
    playhead: 0,
    trackStates: new Map(),
    currentSectionId: null,
    error: null,
    promoModal: { open: false },
    // 53-я
    sidebarOpen: false,
    mode: 'rehearsal',
    shareDialog: { open: false },
    setlistItems: [],
    setlistName: null,
    setlistCreatedAt: null,
    activeShareId: null,
    chartFullscreenOpen: false,
  };
}

let _state: AppState = makeInitialState();
const _subscribers = new Set<(state: AppState) => void>();

export function getState(): AppState {
  return _state;
}

export function setState(patch: Partial<AppState>): void {
  _state = { ..._state, ...patch };
  // Snapshot subscribers so an unsubscribe-during-notify doesn't skip listeners.
  for (const fn of Array.from(_subscribers)) {
    fn(_state);
  }
}

export function subscribe(fn: (state: AppState) => void): () => void {
  _subscribers.add(fn);
  // Fire immediately with the current state so callers don't need a separate
  // `getState()` call on mount.
  fn(_state);
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    _subscribers.delete(fn);
  };
}

export function __resetForTests(): void {
  _state = makeInitialState();
  _subscribers.clear();
}
